import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "src/Utils/API";
import {
  StaffPermissionDto,
  CreateStaffPermissionRequest,
} from "../types/staffPermission";

type ApiResponseV2<T> = {
  success: boolean;
  message: string | null;
  data: T;
};

type ApiResponse = {
  success: boolean;
  message: string | null;
};

const permissionKeys = {
  all: ["staff-permissions"] as const,
};

// GET /web/staff-permissions — Admin only
export const useStaffPermissions = () =>
  useQuery({
    queryKey: permissionKeys.all,
    queryFn: async (): Promise<StaffPermissionDto[]> => {
      const res = await fetch(`${API_URL}/staff-permissions`, {
        credentials: "include",
      });
      const body: ApiResponseV2<StaffPermissionDto[]> = await res.json();
      return body.data;
    },
  });

// POST /web/staff-permissions — Admin only
export const useCreateStaffPermission = (
  onSuccess?: (permission: StaffPermissionDto) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: CreateStaffPermissionRequest
    ): Promise<StaffPermissionDto> => {
      const res = await fetch(`${API_URL}/staff-permissions`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body: ApiResponseV2<StaffPermissionDto> = await res.json();
      if (!body.success)
        throw new Error(body.message ?? "Failed to create permission");
      return body.data;
    },
    onSuccess: (permission) => {
      queryClient.invalidateQueries({ queryKey: permissionKeys.all });
      onSuccess?.(permission);
    },
  });
};

// DELETE /web/staff-permissions/{id} — Admin only
export const useDeleteStaffPermission = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`${API_URL}/staff-permissions/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const body: ApiResponse = await res.json();
      if (!body.success)
        throw new Error(body.message ?? "Failed to delete permission");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permissionKeys.all });
      onSuccess?.();
    },
  });
};
